import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaPlus, FaTrash, FaCheck, FaInfoCircle } from 'react-icons/fa';

const Step3CustomTableModal = ({
  isOpen,
  onClose,
  onSave,
  existingTable,
  isEditMode = false
}) => {
  const COLUMNS = [
    { name: 'Information / Documents Required', isMandatory: false },
    { name: 'Remarks by Client', isMandatory: false }
  ];

  const [rows, setRows] = useState([
    { adminValue: '', isMandatory: true }
  ]);

  useEffect(() => {
    if (isOpen) {
      if (existingTable?.customRows?.length > 0) {
        setRows(existingTable.customRows);
      } else if (existingTable?.rows?.length > 0) {
        setRows(existingTable.rows.map(row => ({
          adminValue: row[0] || '',
          isMandatory: existingTable.columns?.[0]?.isMandatory ?? true
        })));
      } else {
        setRows([{ adminValue: '', isMandatory: true }]);
      }
    }
  }, [isOpen, existingTable]);

  const addRow = () => {
    setRows([...rows, { adminValue: '', isMandatory: true }]);
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== rowIndex));
  };

  const updateAdminValue = (rowIndex, value) => {
    const newRows = [...rows];
    newRows[rowIndex].adminValue = value;
    setRows(newRows);
  };

  const setMandatory = (rowIndex, value) => {
    const newRows = [...rows];
    newRows[rowIndex].isMandatory = value;
    setRows(newRows);
  };

  const handleSave = () => {
    if (rows.some(r => !r.adminValue.trim())) {
      if (!window.confirm("Some rows have empty 'Information / Documents Required' values. Continue?")) {
        return;
      }
    }

    if (isEditMode && !window.confirm('Update this table and send the revised version to the client?')) {
      return;
    }

    onSave({
      columns: COLUMNS.map(col => ({ ...col })),
      rows: rows.map(r => [r.adminValue, '']),
      customRows: rows
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {isEditMode ? 'Edit Step 3 Custom Table' : 'Step 3 Custom Table'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isEditMode
                  ? 'Update the requirements below. The client will need to review and submit again.'
                  : 'Define the information or documents you require from the client.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors shrink-0"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40">
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              <FaInfoCircle className="inline mr-1.5 mb-0.5" />
              Column 1 is for your requirements. Column 2 (&quot;Remarks by Client&quot;) is filled by the client after you save.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Table Structure
            </span>
            <button
              type="button"
              onClick={addRow}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <FaPlus size={10} />
              Add Row
            </button>
          </div>

          <div className="border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 w-10 text-center">
                      #
                    </th>
                    <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-left">
                      Information / Documents Required
                    </th>
                    <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 w-24 text-center">
                      Mandatory
                    </th>
                    <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 w-16 text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/70 border-b border-gray-100 dark:border-dark-700 last:border-b-0"
                    >
                      <td className="px-3 py-2 align-middle text-center text-[11px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">
                        {(rowIndex + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <textarea
                          rows={1}
                          value={row.adminValue}
                          onChange={(e) => updateAdminValue(rowIndex, e.target.value)}
                          placeholder="e.g., W-2 form, 1099-MISC..."
                          className="w-full px-3 py-2 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500 outline-none transition-all resize-none overflow-hidden"
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex justify-center">
                          <div
                            className="inline-flex rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden bg-white dark:bg-dark-800"
                            role="group"
                            aria-label="Mandatory"
                          >
                            <button
                              type="button"
                              onClick={() => setMandatory(rowIndex, false)}
                              className={`min-w-[2.5rem] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                                !row.isMandatory
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                              }`}
                            >
                              No
                            </button>
                            <button
                              type="button"
                              onClick={() => setMandatory(rowIndex, true)}
                              className={`min-w-[2.5rem] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide border-l border-gray-200 dark:border-dark-600 transition-colors ${
                                row.isMandatory
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                              }`}
                            >
                              Yes
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(rowIndex)}
                          disabled={rows.length <= 1}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Delete row"
                        >
                          <FaTrash size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex justify-end gap-2 border-t border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/40">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
          >
            <FaCheck size={12} />
            {isEditMode ? 'Update & Submit to Client' : 'Submit to Client'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Step3CustomTableModal;
